import moment from 'moment';
import downloadFile from 'downloadjs';
import { truncate } from 'lodash';

import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, post } from 'utils/api';
import { logError } from 'utils/log';
import { getGraphDateInterval } from 'utils/graphs/utils';

const { SOV } = API.END_POINTS;

const ANALYSIS_PROCESSING = 'sov/ANALYSIS_PROCESSING';
const ANALYSIS_LOADED = 'sov/ANALYSIS_LOADED';
const ANALYSIS_MARK_AS_RUN = 'sov/ANALYSIS_MARK_AS_RUN';
const ANALYSIS_DOWNLOADING = 'sov/ANALYSIS_DOWNLOADING';
const ANALYSIS_RESET = 'sov/ANALYSIS_RESET';

import type { Action, ApiConfig, Dispatch, ThunkAction } from 'types';
import type {
  SOVAnalysis,
  SOVAnalysisState,
  SOVReport
} from 'modules/insights/sov/types';

const MAX_SOV_REPORT_NAME_LENGTH = 30;

const FORMAT_MAP = {
  spreadsheet: {
    ext: 'xlsx',
    getExportEndpoint: SOV.downloadAnalysis
  }
};

const initialState = {
  analysis: {},
  isLoading: false,
  isDownloading: false,
  analysisHasBeenRun: false
};

const setStatus = (status: boolean): Action => ({
  type: ANALYSIS_PROCESSING,

  payload: {
    status
  }
});

const setDownloadingStatus = (status: boolean): Action => ({
  type: ANALYSIS_DOWNLOADING,

  payload: {
    status
  }
});

const markAnalysisAsRun = (): Action => ({
  type: ANALYSIS_MARK_AS_RUN,
  payload: {}
});

const analysisLoaded = (analysis: Array<SOVAnalysis>): Action => ({
  type: ANALYSIS_LOADED,

  payload: {
    analysis
  }
});

export const resetAnalysis = (): Action => ({
  type: ANALYSIS_RESET
});

export const runAnalysis = (report: SOVReport): ThunkAction => async (dispatch: Dispatch) => {
  dispatch(setStatus(true));
  dispatch(markAnalysisAsRun());

  const updatedData = {
    ...report,
    interval: getGraphDateInterval(report.startDate, report.endDate)
  };

  try {
    const url = SOV.runAnalysis(updatedData.id, report.reportType);
    const response = await post(url, { data: updatedData });
    const analysis = response.data || {};

    dispatch(setStatus(false));
    dispatch(analysisLoaded(analysis));
  } catch (error) {
    logError(error);
  } finally {
    dispatch(setStatus(false));
  }
};

export const downloadAnalysis = (report: SOVReport, graphOverTimeChartImage: string, format: string): ThunkAction => async (dispatch: Dispatch) => {
  dispatch(setDownloadingStatus(true));

  const url = FORMAT_MAP[format].getExportEndpoint(report.id);
  const date = moment().format('YYYY-MM-DD');
  const reportShortName = truncate(report.name, { length: MAX_SOV_REPORT_NAME_LENGTH });
  const fileName = `Collabary - Share of Voice Report - ${date} (${reportShortName}).${FORMAT_MAP[format].ext}`;

  const updatedData = {
    ...report,
    interval: getGraphDateInterval(report.startDate, report.endDate),
    graphOverTimeChartImage
  };

  const requestConfig: Partial<ApiConfig> = {
    responseType: 'blob'
  };

  try {
    const response = await post(url, { data: updatedData, ...requestConfig });

    dispatch(setDownloadingStatus(false));
    downloadFile(response.data, fileName);
  } catch (error) {
    logError(error);
  } finally {
    dispatch(setDownloadingStatus(false));
  }
};

const handleProcessing = (state: SOVAnalysisState, { status }): SOVAnalysisState => ({
  ...state,
  isLoading: status
});

const handleLoaded = (state: SOVAnalysisState, { analysis }): SOVAnalysisState => ({
  ...state,
  analysis,
  isLoading: false
});

const handleDownloading = (state: SOVAnalysisState, { status }): SOVAnalysisState => ({
  ...state,
  isDownloading: status
});

const handleMarkAsRun = (state: SOVAnalysisState): SOVAnalysisState => ({
  ...state,
  analysisHasBeenRun: true
});

export default createReducer<SOVAnalysisState, any>('sovAnalysis', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [ANALYSIS_RESET]: () => initialState,
  [ANALYSIS_PROCESSING]: handleProcessing,
  [ANALYSIS_LOADED]: handleLoaded,
  [ANALYSIS_DOWNLOADING]: handleDownloading,
  [ANALYSIS_MARK_AS_RUN]: handleMarkAsRun
});
